import { Box, Flex, Paper, Text, Button, Badge, Title, Stack } from "@mantine/core";
import {
  IconBuilding,
  IconPhone,
  IconMail,
  IconBookmark,
  IconRosetteDiscountCheckFilled,
  IconCheck,
  IconX,
  IconDownload,
} from "@tabler/icons-react";
import ProfileVerificationStepper from "@/pages/profile-verification/ProfileVerificationStepper";
import { getCollaborationStatusConfig } from "@/shared/utils/collaboration-helpers";

type CompanyHeaderCardProps = {
  company: any;
  headquartersFlagEmoji: string | null;
  showProfileVerificationStepper: boolean;
  hideInterestCTA: boolean;
  disableShortlist: boolean;
  isFavorited: boolean;
  collaborationStatus: any;
  userCompanyId?: string;
  canIncomingReceiverDownloadAnonymizedPdf: boolean;
  anonymizedPdfLoading: boolean;
  declineLoading: boolean;
  onExpressInterestClick: () => void;
  onShortlistClick: () => void;
  onAcceptRequestClick: () => void;
  onDeclineClick: () => void;
  onDownloadAnonymizedProfilePdf: () => void;
  bookmarkButtonStyles: Record<string, unknown>;
};

export default function CompanyHeaderCard({
  company,
  headquartersFlagEmoji,
  showProfileVerificationStepper,
  hideInterestCTA,
  disableShortlist,
  isFavorited,
  collaborationStatus,
  userCompanyId,
  canIncomingReceiverDownloadAnonymizedPdf,
  anonymizedPdfLoading,
  declineLoading,
  onExpressInterestClick,
  onShortlistClick,
  onAcceptRequestClick,
  onDeclineClick,
  onDownloadAnonymizedProfilePdf,
  bookmarkButtonStyles,
}: CompanyHeaderCardProps) {
  const renderMobileCollaborationChip = () => {
    if (!collaborationStatus) return null;
    if (
      collaborationStatus.status === "PAYMENT_EXPIRED" ||
      collaborationStatus.status === "BOTH_PAID" ||
      collaborationStatus.status === "RECEIVER_DECLINED"
    ) {
      return null;
    }

    const isReceiver = userCompanyId === collaborationStatus.receiverCompanyId;
    const isRequested = collaborationStatus.status === "REQUESTED";
    if (isRequested && isReceiver) return null;

    const chipConfig = userCompanyId
      ? getCollaborationStatusConfig(
          collaborationStatus.status,
          userCompanyId === collaborationStatus.requesterCompanyId,
          collaborationStatus.requesterPaid,
          collaborationStatus.receiverPaid
        )
      : getCollaborationStatusConfig(collaborationStatus.status);

    return (
      <Box
        px={10}
        py={5}
        style={{
          borderRadius: "var(--mantine-radius-md)",
          backgroundColor: chipConfig.bg,
          color: chipConfig.text,
          fontSize: "12px",
          fontWeight: 600,
          whiteSpace: "nowrap",
        }}
      >
        {chipConfig.label}
      </Box>
    );
  };

  const renderIncomingRequestActionsMobile = () => {
    if (
      !collaborationStatus ||
      collaborationStatus.status === "PAYMENT_EXPIRED" ||
      collaborationStatus.status === "BOTH_PAID" ||
      collaborationStatus.status === "RECEIVER_DECLINED"
    ) {
      return null;
    }

    const isReceiver = userCompanyId === collaborationStatus.receiverCompanyId;
    const isRequested = collaborationStatus.status === "REQUESTED";
    if (!(isRequested && isReceiver)) return null;

    return (
      <Flex direction="column" gap="xs" w="100%" mt="sm">
        <Flex gap="xs" w="100%">
          <Button size="xs" color="primary" leftSection={<IconCheck size={16} />} onClick={onAcceptRequestClick} style={{ flex: 1 }}>
            Accept
          </Button>
          <Button
            size="xs"
            variant="outline"
            leftSection={<IconX size={16} />}
            onClick={onDeclineClick}
            loading={declineLoading}
            style={{ flex: 1 }}
          >
            Decline
          </Button>
        </Flex>
        {canIncomingReceiverDownloadAnonymizedPdf && (
          <Button
            variant="outline"
            color="primary"
            size="xs"
            fullWidth
            leftSection={<IconDownload size={16} />}
            loading={anonymizedPdfLoading}
            onClick={onDownloadAnonymizedProfilePdf}
          >
            Download Profile PDF
          </Button>
        )}
      </Flex>
    );
  };

  return (
    <>
      <Paper
        p="md"
        radius="lg"
        mb="md"
        style={{
          border: "1px solid #9CB1D8",
          backgroundColor: "#E9EEFA",
          boxShadow: "0 1px 3px rgba(15, 23, 42, 0.08)",
          maxWidth: "100%",
          overflow: "hidden",
        }}
        hiddenFrom="sm"
      >
        <Flex gap="sm" align="flex-start" w="100%" wrap="nowrap" miw={0}>
          <Flex
            w={48}
            h={48}
            style={{
              borderRadius: 8,
              border: "1px solid #8EA6CF",
              backgroundColor: "#F3F6FC",
              flexShrink: 0,
              overflow: "hidden",
            }}
            align="center"
            justify="center"
          >
            <IconBuilding size={24} color="#6B7280" />
          </Flex>

          <Flex direction="column" style={{ flex: 1, minWidth: 0 }}>
            <Flex justify="space-between" align="flex-start" gap="xs">
              <Box style={{ minWidth: 0 }}>
                <Flex align="center" gap={6} wrap="nowrap">
                  <Text size="xl" fw={500} c="#334155" style={{ lineHeight: 1.2 }}>
                    {company.gtid}
                  </Text>
                  {company.status === "VERIFIED" && (
                    <IconRosetteDiscountCheckFilled size={18} color="var(--mantine-color-primary-6)" />
                  )}
                </Flex>
              </Box>

              <Flex direction="column" gap="xs" align="flex-end">
                {!hideInterestCTA && (collaborationStatus ? renderMobileCollaborationChip() : null)}
                {!disableShortlist && (
                  <Button
                    variant="filled"
                    size="md"
                    p="xs"
                    onClick={onShortlistClick}
                    styles={bookmarkButtonStyles}
                    style={{ flexShrink: 0 }}
                  >
                    <IconBookmark size={20} color="white" fill={isFavorited ? "currentColor" : "none"} />
                  </Button>
                )}
              </Flex>
            </Flex>

            <Badge
              variant="light"
              color="#EEF2F7"
              size="sm"
              radius="sm"
              mt={8}
              style={{
                width: "fit-content",
                maxWidth: "100%",
                backgroundColor: "#EEF2F7",
                color: "#374151",
              }}
            >
              {company.gtid}
            </Badge>

            <Stack gap={2} mt={8}>
              <Flex align="center" gap="xs" wrap="nowrap" miw={0}>
                {headquartersFlagEmoji ? (
                  <Text
                    component="span"
                    fz={16}
                    lh={1}
                    role="img"
                    aria-label={`${company.headquartersCountry ?? "Country"} flag`}
                    style={{ flexShrink: 0 }}
                  >
                    {headquartersFlagEmoji}
                  </Text>
                ) : null}
                <Text size="sm" c="slate.7" style={{ minWidth: 0, wordBreak: "break-word" }}>
                  {company.headquartersCountry || "India"}
                </Text>
              </Flex>
              <Flex align="center" gap="xs" wrap="nowrap" miw={0}>
                <IconPhone size={14} color="#6B7280" style={{ flexShrink: 0 }} />
                <Text size="sm" c="slate.7" style={{ minWidth: 0, wordBreak: "break-word" }}>
                  {company.phone || "XXXX XXXXX"}
                </Text>
              </Flex>
              <Flex align="center" gap="xs" wrap="nowrap" miw={0}>
                <IconMail size={14} color="#6B7280" style={{ flexShrink: 0 }} />
                <Text size="sm" c="slate.7" style={{ minWidth: 0, wordBreak: "break-all" }}>
                  {"XXXXX@mail.com"}
                </Text>
              </Flex>
            </Stack>

            {showProfileVerificationStepper && (
              <Box mt="sm" style={{ width: "100%", minWidth: 0 }}>
                <ProfileVerificationStepper status={company.status} compact />
              </Box>
            )}
          </Flex>
        </Flex>

        {!hideInterestCTA && !collaborationStatus ? (
          <Button
            fullWidth
            mt="sm"
            color="primary"
            radius="md"
            size="sm"
            onClick={onExpressInterestClick}
            styles={{
              root: {
                backgroundColor: "#04305c",
                border: "1px solid #04305c",
                color: "#ffffff",
                fontWeight: 600,
              },
            }}
          >
            Express Interest
          </Button>
        ) : null}

        {renderIncomingRequestActionsMobile()}
      </Paper>

      <Paper
        p="xl"
        radius="lg"
        mb="lg"
        style={{
          border: "1px solid #04305c",
          backgroundColor: "#ebf0ff",
          boxShadow: "0 1px 3px rgba(15, 23, 42, 0.08)",
        }}
        visibleFrom="sm"
      >
        <Flex gap="xl" align="flex-start" justify="space-between">
          <Flex gap="md" align="flex-start">
            <Flex
              w={80}
              h={80}
              style={{
                borderRadius: 8,
                border: "1px solid #04305c",
                backgroundColor: "#F9FAFB",
                flexShrink: 0,
                overflow: "hidden",
              }}
              align="center"
              justify="center"
            >
              <IconBuilding size={40} color="#6B7280" />
            </Flex>

            <Flex direction="column">
              <Flex align="center" gap="sm" mb="xs" wrap="wrap">
                <Title order={2} fw={400} fz={22}>
                  {company.gtid}
                </Title>
                {company.status === "VERIFIED" && (
                  <IconRosetteDiscountCheckFilled size={20} color="var(--mantine-color-primary-6)" />
                )}
              </Flex>
              <Flex gap="md" mb="xs" wrap="wrap">
                <Flex align="center" gap="xs">
                  {headquartersFlagEmoji ? (
                    <Text component="span" fz={20} lh={1} role="img" aria-label={`${company.headquartersCountry ?? "Country"} flag`}>
                      {headquartersFlagEmoji}
                    </Text>
                  ) : null}
                  <Text size="l" c="slate.7">
                    {company.headquartersCountry || "N/A"}
                  </Text>
                </Flex>
                <Flex align="center" gap="xs">
                  <IconPhone size={20} color="#6B7280" />
                  <Text size="l" c="slate.7">
                    {company.phone || "XXXX XXXXX"}
                  </Text>
                </Flex>
                <Flex align="center" gap="xs">
                  <IconMail size={20} color="#6B7280" />
                  <Text size="l" c="slate.7">
                    XXX@mail.com
                  </Text>
                </Flex>
              </Flex>
            </Flex>
          </Flex>

          <Flex direction="column" align="flex-end" gap="sm" style={{ flexShrink: 0 }}>
            {showProfileVerificationStepper && <ProfileVerificationStepper status={company.status} compact />}
            {!hideInterestCTA &&
              (!collaborationStatus ? (
                <Flex direction="row" align="center" gap="sm" wrap="nowrap">
                  <Button size="md" color="primary" onClick={onExpressInterestClick}>
                    Express Interest
                  </Button>
                  <Button variant="filled" size="md" p="xs" onClick={onShortlistClick} styles={bookmarkButtonStyles}>
                    <IconBookmark size={24} color="white" fill={isFavorited ? "currentColor" : "none"} />
                  </Button>
                </Flex>
              ) : (
                <Flex direction="row" align="center" gap="sm" wrap="nowrap">
                  {collaborationStatus.status !== "PAYMENT_EXPIRED" &&
                    collaborationStatus.status !== "BOTH_PAID" &&
                    collaborationStatus.status !== "RECEIVER_DECLINED" &&
                    (() => {
                      const isReceiver = userCompanyId === collaborationStatus.receiverCompanyId;
                      const isRequested = collaborationStatus.status === "REQUESTED";

                      if (isRequested && isReceiver) {
                        return (
                          <Flex gap="sm" align="center" wrap="wrap" justify="flex-end">
                            <Button color="primary" leftSection={<IconCheck size={20} />} onClick={onAcceptRequestClick}>
                              Accept
                            </Button>
                            <Button variant="outline" color="gray" leftSection={<IconX size={20} />} onClick={onDeclineClick} loading={declineLoading}>
                              Decline
                            </Button>
                            {canIncomingReceiverDownloadAnonymizedPdf && (
                              <Button
                                variant="outline"
                                color="primary"
                                size="md"
                                leftSection={<IconDownload size={18} />}
                                loading={anonymizedPdfLoading}
                                onClick={onDownloadAnonymizedProfilePdf}
                              >
                                Download Profile PDF
                              </Button>
                            )}
                          </Flex>
                        );
                      }

                      const chipConfig = userCompanyId
                        ? getCollaborationStatusConfig(
                            collaborationStatus.status,
                            userCompanyId === collaborationStatus.requesterCompanyId,
                            collaborationStatus.requesterPaid,
                            collaborationStatus.receiverPaid
                          )
                        : getCollaborationStatusConfig(collaborationStatus.status);

                      return (
                        <Box
                          px={16}
                          py={7}
                          style={{
                            borderRadius: "var(--mantine-radius-md)",
                            backgroundColor: chipConfig.bg,
                            color: chipConfig.text,
                            fontSize: "13px",
                            fontWeight: 500,
                            whiteSpace: "nowrap",
                          }}
                        >
                          {chipConfig.label}
                        </Box>
                      );
                    })()}
                  <Button variant="filled" size="md" p="xs" onClick={onShortlistClick} styles={bookmarkButtonStyles}>
                    <IconBookmark size={24} color="white" fill={isFavorited ? "currentColor" : "none"} />
                  </Button>
                </Flex>
              ))}
          </Flex>
        </Flex>
      </Paper>
    </>
  );
}
